import { useEffect, useRef, useState } from 'react';
import { useFetcher } from 'react-router';
import { VENUE_FILTER_FETCHER_KEY } from '~/components/home/venue-selector.tsx';
import type { HomeReservation } from '~/routes/_index/fetch-home-reservations.server.ts';
import type { loader as homeReservationsLoader } from '~/routes/home-reservations.ts';

type UseReservationsArgs = {
  initialReservations: HomeReservation[];
  initialHasMore: boolean;
  selectedVenueId: string | null;
};

export const useReservations = ({
  initialReservations,
  initialHasMore,
  selectedVenueId,
}: UseReservationsArgs) => {
  const venueFilterFetcher = useFetcher({ key: VENUE_FILTER_FETCHER_KEY });
  const loadMoreFetcher = useFetcher<typeof homeReservationsLoader>();
  const [reservations, setReservations] = useState(initialReservations);
  const [hasMore, setHasMore] = useState(initialHasMore);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const isRefreshing = venueFilterFetcher.state !== 'idle';
  const isLoadingMore = loadMoreFetcher.state === 'loading';

  // oxlint-disable-next-line react-doctor/no-derived-state-effect -- syncs paginated list when loader data resets after venue filter changes
  useEffect(() => {
    setReservations(initialReservations);
    setHasMore(initialHasMore);
  }, [initialHasMore, initialReservations]);

  useEffect(() => {
    const fetcherData = loadMoreFetcher.data;
    if (!fetcherData || !('reservations' in fetcherData)) {
      return;
    }

    const nextReservations = fetcherData.reservations;

    setReservations(previous => {
      const existingIds = new Set(previous.map(reservation => reservation.id));
      const appended = nextReservations.filter(reservation => !existingIds.has(reservation.id));

      return [...previous, ...appended];
    });
    setHasMore(fetcherData.hasMore);
  }, [loadMoreFetcher.data]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || !('IntersectionObserver' in globalThis)) {
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (!entry?.isIntersecting || isLoadingMore || isRefreshing) {
          return;
        }

        const params = new URLSearchParams();
        params.set('skip', String(reservations.length));
        if (selectedVenueId) {
          params.set('venueId', selectedVenueId);
        }

        loadMoreFetcher.load(`/home-reservations?${params.toString()}`);
      },
      { rootMargin: '120px' },
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, isLoadingMore, isRefreshing, loadMoreFetcher, reservations.length, selectedVenueId]);

  return {
    reservations,
    hasMore,
    isRefreshing,
    isLoadingMore,
    sentinelRef,
  };
};
